import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import Swal from 'sweetalert2';
import { Subscription } from 'rxjs';

import { TranslocoService } from '@ngneat/transloco';

import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { DoctorsService } from '../../../../services/doctors';

@Component({
  selector: 'app-applyany',
  templateUrl: './applyany.component.html',
  styleUrl: './applyany.component.scss'
})
export class ApplyanyComponent implements OnInit, OnDestroy {
  data!: FormGroup;
  dataForm: FormData = new FormData();

  contentReady = false;
  isSubmitting = false;

  isMobile = false;
  isAr = false;

  editId!: number;
  isUpdatePage = false;

  currentStep = 0;

  selectedImage: File | null = null;
  selectedCertificates: File[] = [];

  imagePreviewUrl: string | null = null;
  existingImageUrl: string | null = null;

  imageErrorMessage = '';
  certificateErrorMessage = '';
  formErrorMessage = '';
  backendValidationErrors: string[] = [];

  private imageObjectUrl: string | null = null;

  private readonly maxImageSize = 2 * 1024 * 1024;
  private readonly maxCertificateSize = 5 * 1024 * 1024;

  readonly maxCertificatesCount = 20;

  private readonly apiBaseUrl = 'https://backendtest.blueboxpet.com';

  private readonly allowedCertificateExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.webp',
    '.pdf'
  ];

  private readonly fieldLabelKeys: Record<string, string> = {
    name: 'applyAny.fields.name',
    mobile: 'applyAny.fields.mobile',
    email: 'applyAny.fields.email',
    specialty: 'applyAny.fields.specialty',
    qualification: 'applyAny.fields.qualification',
    university: 'applyAny.fields.university',
    graduation_year: 'applyAny.fields.graduationYear',
    residence_place: 'applyAny.fields.residencePlace',
    birth_place: 'applyAny.fields.birthPlace',
    birth_date: 'applyAny.fields.birthDate',
    phd_source: 'applyAny.fields.phdSource',
    phd_year: 'applyAny.fields.phdYear',
    master_source: 'applyAny.fields.masterSource',
    master_year: 'applyAny.fields.masterYear',
    fellowship: 'applyAny.fields.fellowship',
    classification_status: 'applyAny.fields.classificationStatus',
    notes: 'applyAny.fields.notes'
  };

  private readonly validationOrder = [
    'name',
    'mobile',
    'email',
    'specialty',
    'qualification',
    'university',
    'graduation_year',
    'residence_place',
    'birth_place',
    'birth_date',
    'phd_source',
    'phd_year',
    'master_source',
    'master_year',
    'fellowship',
    'classification_status',
    'notes'
  ];

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private doctorService: DoctorsService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.setLanguageDirection();

    const langSub = this.translocoService.langChanges$.subscribe(() => {
      this.setLanguageDirection();
    });

    this.subscriptions.add(langSub);

    this.initForm();
    this.setupDegreeFields();

    this.route.queryParams.subscribe(params => {
      if (params['job_type'] !== undefined) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { job_type: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }

      if (params['id']) {
        this.isUpdatePage = true;
        this.editId = +params['id'];

        this.data.patchValue({
          name: params['name'] || '',
          mobile: params['mobile'] || '',
          email: params['email'] || '',
          specialty: params['specialty'] || '',
          qualification: params['qualification'] || '',

          university: params['university'] || '',
          graduation_year: params['graduation_year'] || null,

          residence_place: params['residence_place'] || '',
          birth_place: params['birth_place'] || '',
          birth_date: params['birth_date'] ? String(params['birth_date']).split('T')[0] : '',

          has_phd: this.asBool(params['has_phd']),
          phd_source: params['phd_source'] || '',
          phd_year: params['phd_year'] || null,

          has_master: this.asBool(params['has_master']),
          master_source: params['master_source'] || '',
          master_year: params['master_year'] || null,

          fellowship: params['fellowship'] || '',
          classification_status: params['classification_status'] || '',

          ksa_experience: this.asBool(params['ksa_experience']),
          transfer_of_sponsorship: this.asBool(params['transfer_of_sponsorship']),
          offer: this.asBool(params['offer']),
          visa: this.asBool(params['visa']),
          has_dataflow: this.asBool(params['has_dataflow']),

          notes: params['notes'] || '',
          is_doctor: this.asBool(params['is_doctor'])
        });

        const imageFromParams =
          params['doctor_image'] ||
          params['image'] ||
          params['image_url'] ||
          params['doctor_image_url'] ||
          null;

        this.existingImageUrl = this.fixFileUrl(imageFromParams);
        this.imagePreviewUrl = this.existingImageUrl;
      }

      this.contentReady = true;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.revokeImageObjectUrl();
  }

  initForm(): void {
    this.data = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(255)
        ]
      ],

      mobile: [
        '',
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(/^[0-9+\-\s()]{7,30}$/)
        ]
      ],

      email: ['', [Validators.email, Validators.maxLength(255)]],
      specialty: ['', [Validators.maxLength(255)]],
      qualification: ['', [Validators.maxLength(255)]],

      university: ['', [Validators.maxLength(255)]],
      graduation_year: [null, [Validators.min(1940), Validators.max(2100)]],

      residence_place: ['', [Validators.maxLength(255)]],
      birth_place: ['', [Validators.maxLength(255)]],
      birth_date: [''],

      has_phd: [false],
      phd_source: [''],
      phd_year: [null],

      has_master: [false],
      master_source: [''],
      master_year: [null],

      fellowship: ['', [Validators.maxLength(255)]],
      classification_status: ['', [Validators.maxLength(255)]],

      ksa_experience: [false],
      transfer_of_sponsorship: [false],
      offer: [false],
      visa: [false],
      has_dataflow: [false],

      notes: ['', [Validators.maxLength(3000)]],

      add_by: [localStorage.getItem('username') || 'admin'],
      is_doctor: [false]
    });
  }

  getSubmitButtonText(): string {
    if (this.isSubmitting) {
      return this.t('applyAny.buttons.saving');
    }

    return this.isUpdatePage
      ? this.t('applyAny.buttons.update')
      : this.t('applyAny.buttons.create');
  }

  nextStep(): void {
    this.resetErrors();

    if (this.currentStep === 0) {
      const firstStepControls = [
        'name',
        'mobile',
        'email',
        'qualification',
        'graduation_year',
        'birth_date'
      ];

      const invalidControl = firstStepControls.find(controlName => {
        return this.data.get(controlName)?.invalid;
      });

      if (invalidControl) {
        firstStepControls.forEach(controlName => {
          this.data.get(controlName)?.markAsTouched();
        });

        this.formErrorMessage = this.getError(invalidControl);
        this.scrollToFirstInvalid();
        return;
      }
    }

    if (this.currentStep === 1) {
      const secondStepControls = this.getSecondStepControls();

      const invalidControl = secondStepControls.find(controlName => {
        return this.data.get(controlName)?.invalid;
      });

      if (invalidControl) {
        secondStepControls.forEach(controlName => {
          this.data.get(controlName)?.markAsTouched();
        });

        this.formErrorMessage = this.getError(invalidControl);
        this.scrollToFirstInvalid();
        return;
      }
    }

    this.currentStep = Math.min(this.currentStep + 1, 2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  previousStep(): void {
    this.resetErrors();
    this.currentStep = Math.max(this.currentStep - 1, 0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goBack(): void {
    this.router.navigateByUrl('/alljobs');
  }

  onSubmit(): void {
    this.resetErrors();

    if (this.data.invalid) {
      this.data.markAllAsTouched();

      const firstError = this.getFirstFormError();
      this.formErrorMessage = firstError;

      Swal.fire({
        icon: 'error',
        title: this.t('applyAny.alerts.validationTitle'),
        text: firstError,
        confirmButtonText: this.t('applyAny.buttons.ok')
      });

      this.scrollToFirstInvalid();
      return;
    }

    if (this.selectedCertificates.length > this.maxCertificatesCount) {
      this.certificateErrorMessage = this.t('applyAny.validation.maxCertificates', {
        max: this.maxCertificatesCount
      });

      this.formErrorMessage = this.certificateErrorMessage;
      return;
    }

    const payload = this.buildFormData();

    if (this.isUpdatePage) {
      this.onEdit(payload);
    } else {
      this.onAdd(payload);
    }
  }

  private buildFormData(): FormData {
    const fd = new FormData();

    const formValue = {
      ...this.data.getRawValue(),
      add_by: localStorage.getItem('username') || 'admin'
    };

    Object.entries(formValue).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        fd.append(key, value ? '1' : '0');
        return;
      }

      if (value !== null && value !== undefined && value !== '') {
        fd.append(key, String(value));
      }
    });

    if (this.selectedImage) {
      fd.append('doctor_image', this.selectedImage);
      fd.append('image', this.selectedImage);
    }

    this.selectedCertificates.forEach(file => {
      fd.append('certificates[]', file);
    });

    return fd;
  }

  onAdd(payload: FormData): void {
    this.isSubmitting = true;
    this.contentReady = false;

    this.doctorService.create(payload).subscribe({
      next: () => {
        this.isSubmitting = false;

        Swal.fire({
          icon: 'success',
          title: this.t('applyAny.alerts.createdSuccess'),
          showConfirmButton: false,
          timer: 1500
        });

        this.router.navigateByUrl('/alljobs');
      },
      error: err => {
        this.isSubmitting = false;
        this.contentReady = true;
        this.showServerError(err);
      }
    });
  }

  onEdit(payload: FormData): void {
    this.isSubmitting = true;
    this.contentReady = false;

    const username = localStorage.getItem('username') || 'Unknown';
    payload.append('editor', username);

    this.doctorService.update(this.editId, payload).subscribe({
      next: () => {
        this.isSubmitting = false;

        Swal.fire({
          icon: 'success',
          title: this.t('applyAny.alerts.updatedSuccess'),
          showConfirmButton: false,
          timer: 1500
        });

        this.router.navigateByUrl('/alljobs');
      },
      error: err => {
        this.isSubmitting = false;
        this.contentReady = true;
        this.showServerError(err);
      }
    });
  }

  onImageChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    input.value = '';

    this.imageErrorMessage = '';
    this.formErrorMessage = '';

    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      this.selectedImage = null;
      this.imagePreviewUrl = this.existingImageUrl;

      this.imageErrorMessage = this.t('applyAny.validation.invalidImageType');
      this.formErrorMessage = this.imageErrorMessage;
      return;
    }

    if (file.size > this.maxImageSize) {
      this.selectedImage = null;
      this.imagePreviewUrl = this.existingImageUrl;

      this.imageErrorMessage = this.t('applyAny.validation.imageMaxSize');
      this.formErrorMessage = this.imageErrorMessage;
      return;
    }

    this.selectedImage = file;
    this.revokeImageObjectUrl();

    this.imageObjectUrl = URL.createObjectURL(file);
    this.imagePreviewUrl = this.imageObjectUrl;
  }

  removeImage(): void {
    this.selectedImage = null;
    this.imageErrorMessage = '';
    this.revokeImageObjectUrl();
    this.imagePreviewUrl = this.existingImageUrl;
  }

  onCertificatesChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';

    this.certificateErrorMessage = '';
    this.formErrorMessage = '';

    if (!files.length) return;

    const totalAfterAdd = this.selectedCertificates.length + files.length;

    if (totalAfterAdd > this.maxCertificatesCount) {
      this.certificateErrorMessage = this.t('applyAny.validation.maxCertificates', {
        max: this.maxCertificatesCount
      });

      this.formErrorMessage = this.certificateErrorMessage;
      return;
    }

    for (const file of files) {
      if (!this.isValidCertificateFile(file)) {
        this.certificateErrorMessage = this.t('applyAny.validation.invalidCertificateType');
        this.formErrorMessage = this.certificateErrorMessage;
        return;
      }

      if (file.size > this.maxCertificateSize) {
        this.certificateErrorMessage = this.t('applyAny.validation.certificateMaxSize', {
          fileName: file.name
        });

        this.formErrorMessage = this.certificateErrorMessage;
        return;
      }
    }

    this.selectedCertificates = [...this.selectedCertificates, ...files];
  }

  removeCertificate(index: number): void {
    this.selectedCertificates = this.selectedCertificates.filter(
      (_, i) => i !== index
    );

    this.certificateErrorMessage = '';
    this.formErrorMessage = '';
  }

  clearCertificates(): void {
    this.selectedCertificates = [];
    this.certificateErrorMessage = '';
    this.formErrorMessage = '';
  }

  trackByFile(index: number, file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}-${index}`;
  }

  isInvalid(controlName: string): boolean {
    const control = this.data.get(controlName);

    return Boolean(
      control &&
      control.invalid &&
      (control.dirty || control.touched)
    );
  }

  getError(controlName: string): string {
    const control = this.data.get(controlName);

    if (!control || !control.errors) return '';

    const label = this.getFieldLabel(controlName);

    if (control.errors['required']) {
      return this.t('applyAny.validation.required', { field: label });
    }

    if (control.errors['email']) {
      return this.t('applyAny.validation.email', { field: label });
    }

    if (control.errors['minlength']) {
      return this.t('applyAny.validation.minLength', {
        field: label,
        requiredLength: control.errors['minlength'].requiredLength
      });
    }

    if (control.errors['maxlength']) {
      return this.t('applyAny.validation.maxLength', {
        field: label,
        requiredLength: control.errors['maxlength'].requiredLength
      });
    }

    if (control.errors['min']) {
      return this.t('applyAny.validation.min', {
        field: label,
        min: control.errors['min'].min
      });
    }

    if (control.errors['max']) {
      return this.t('applyAny.validation.max', {
        field: label,
        max: control.errors['max'].max
      });
    }

    if (control.errors['pattern']) {
      if (controlName === 'mobile') {
        return this.t('applyAny.validation.mobilePattern');
      }

      return this.t('applyAny.validation.invalid', { field: label });
    }

    return this.t('applyAny.validation.invalid', { field: label });
  }

  private setupDegreeFields(): void {
    this.updateDegreeControls(
      'master_source',
      'master_year',
      Boolean(this.data.get('has_master')?.value)
    );

    const masterSub = this.data.get('has_master')?.valueChanges.subscribe(value => {
      this.updateDegreeControls('master_source', 'master_year', Boolean(value));
    });

    if (masterSub) this.subscriptions.add(masterSub);
  }

  private updateDegreeControls(
    sourceControlName: string,
    yearControlName: string,
    checked: boolean
  ): void {
    const sourceControl = this.data.get(sourceControlName);
    const yearControl = this.data.get(yearControlName);

    if (!sourceControl || !yearControl) return;

    if (checked) {
      sourceControl.setValidators([
        Validators.required,
        Validators.maxLength(255)
      ]);

      yearControl.setValidators([
        Validators.required,
        Validators.min(1940),
        Validators.max(2100)
      ]);
    } else {
      sourceControl.clearValidators();
      yearControl.clearValidators();

      sourceControl.setValue('', { emitEvent: false });
      yearControl.setValue(null, { emitEvent: false });
    }

    sourceControl.updateValueAndValidity({ emitEvent: false });
    yearControl.updateValueAndValidity({ emitEvent: false });
  }

  private getSecondStepControls(): string[] {
    const controls = [
      'fellowship',
      'classification_status'
    ];

    if (this.data.get('has_master')?.value) {
      controls.push('master_source', 'master_year');
    }

    return controls;
  }

  private getFirstFormError(): string {
    for (const controlName of this.validationOrder) {
      const control = this.data.get(controlName);

      if (control?.invalid) {
        return this.getError(controlName);
      }
    }

    return this.t('applyAny.validation.reviewRequiredData');
  }

  private showServerError(err: any): void {
    const laravelErrors = err?.error?.errors;

    this.backendValidationErrors = [];

    if (laravelErrors) {
      Object.keys(laravelErrors).forEach(key => {
        const messages = laravelErrors[key];

        if (Array.isArray(messages)) {
          messages.forEach(message => {
            this.backendValidationErrors.push(
              `${this.translateServerField(key)}: ${message}`
            );
          });
        }
      });

      this.formErrorMessage =
        this.backendValidationErrors[0] ||
        this.t('applyAny.alerts.validationServerError');

      Swal.fire({
        icon: 'error',
        title: this.t('applyAny.alerts.serverErrorTitle'),
        text: this.formErrorMessage,
        confirmButtonText: this.t('applyAny.buttons.ok')
      });

      return;
    }

    const message =
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      this.t('applyAny.alerts.saveFailed');

    this.formErrorMessage = message;

    Swal.fire({
      icon: 'error',
      title: this.t('applyAny.alerts.errorTitle'),
      text: message,
      confirmButtonText: this.t('applyAny.buttons.ok')
    });
  }

  private translateServerField(key: string): string {
    const cleanKey = key.replace(/\.\d+/g, '');

    const map: Record<string, string> = {
      name: 'applyAny.fields.name',
      mobile: 'applyAny.fields.mobile',
      email: 'applyAny.fields.email',
      specialty: 'applyAny.fields.specialty',
      qualification: 'applyAny.fields.qualification',
      university: 'applyAny.fields.university',
      graduation_year: 'applyAny.fields.graduationYear',
      has_master: 'applyAny.fields.hasMaster',
      master_source: 'applyAny.fields.masterSource',
      master_year: 'applyAny.fields.masterYear',
      has_phd: 'applyAny.fields.hasPhd',
      phd_source: 'applyAny.fields.phdSource',
      phd_year: 'applyAny.fields.phdYear',
      fellowship: 'applyAny.fields.fellowship',
      classification_status: 'applyAny.fields.classificationStatus',
      ksa_experience: 'applyAny.fields.ksaExperience',
      residence_place: 'applyAny.fields.residencePlace',
      birth_place: 'applyAny.fields.birthPlace',
      birth_date: 'applyAny.fields.birthDate',
      transfer_of_sponsorship: 'applyAny.fields.transferSponsorship',
      offer: 'applyAny.fields.offer',
      visa: 'applyAny.fields.visa',
      has_dataflow: 'applyAny.fields.hasDataflow',
      notes: 'applyAny.fields.notes',
      image: 'applyAny.fields.image',
      doctor_image: 'applyAny.fields.image',
      certificates: 'applyAny.fields.certificates',
      'certificates.*': 'applyAny.fields.certificatesFiles',
      add_by: 'applyAny.fields.addBy',
      is_doctor: 'applyAny.fields.registrationType'
    };

    return map[cleanKey] ? this.t(map[cleanKey]) : cleanKey;
  }

  private getFieldLabel(controlName: string): string {
    const key = this.fieldLabelKeys[controlName];

    return key ? this.t(key) : controlName;
  }

  private scrollToFirstInvalid(): void {
    setTimeout(() => {
      const firstInvalid = document.querySelector('.is-invalid');

      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }, 100);
  }

  private resetErrors(): void {
    this.formErrorMessage = '';
    this.backendValidationErrors = [];
    this.imageErrorMessage = '';
    this.certificateErrorMessage = '';
  }

  private isValidCertificateFile(file: File): boolean {
    const fileName = file.name.toLowerCase();

    return this.allowedCertificateExtensions.some(extension =>
      fileName.endsWith(extension)
    );
  }

  private asBool(value: any): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private fixFileUrl(path: string | null): string | null {
    if (!path) return null;

    let value = String(path).trim();

    if (!value) return null;

    if (value.startsWith('http://') || value.startsWith('https://')) {
      if (value.includes('/storage/app/public/')) {
        return value;
      }

      if (value.includes('/storage/')) {
        return value.replace('/storage/', '/storage/app/public/');
      }

      return value;
    }

    value = value
      .replace(/^\/+/, '')
      .replace(/^storage\/app\/public\//, '')
      .replace(/^storage\//, '')
      .replace(/^app\/public\//, '')
      .replace(/^public\//, '');

    return `${this.apiBaseUrl}/storage/app/public/${value}`;
  }

  private revokeImageObjectUrl(): void {
    if (this.imageObjectUrl) {
      URL.revokeObjectURL(this.imageObjectUrl);
      this.imageObjectUrl = null;
    }
  }

  private setLanguageDirection(): void {
    const activeLang =
      this.translocoService.getActiveLang() ||
      localStorage.getItem('language') ||
      'ar';

    this.isAr = activeLang === 'ar';
  }

  private t(key: string, params?: Record<string, any>): string {
    return this.translocoService.translate(key, params);
  }
}
